import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { Box, CircularProgress } from '@mui/material';
import log from 'loglevel';
import { useTranslation } from 'react-i18next';
import { validateBasicDetails } from '../validateBasicDetails';
import { usePostTalent } from '../../../hooks/useTalent';
import { ROUTES, SOCIAL_LINKS_VALUES } from '../../../consts';
import { createTalentObject } from '../utils';
import useExitPrompt from '../../../hooks/useExitPrompt';
import { ContactDetail } from '../../../components/ContactDetailsModal';
import { ErrorsStack } from '../../../components/ErrorsStack';
import { HistoryMainPanel } from '../../../components/HistoryMainPanel';
import { useHistoryLogsTalent } from '../../../hooks/useHistoryLogsTalent';
import STRING_KEYS from '../../../language/keys';
import { useAuth } from '../../../hooks/useAuth';
import { PERMISSIONS } from '../../../consts/permissions';
import { VIEW_MODE } from '../../../consts/enums';
import TalentBasicDetails from './TalentBasicDetails';
import TalentSocialLinks from './TalentsSocialLinkSection/TalentSocialLinks';
import TalentSkills from './TalentSkills';
import TalentKeyAttributes from './TalentKeyAttributes';
import TalentEditHeader from './TalentsEditHeader/TalentsEditHeader';
import TalentExperience from './TalentExperience';
import TalentEducation from './TalentEducation';
import { TalentLeftNavBar } from './TalentLeftNavBar';
import TalentContactInfo from './TalentContactInfo';
import BreadCrumbs from './BreadCrumbs';
import ManualCompanyModal from './ManualCompanyModal';
import ManualEducationModal from './ManualEducationModal';

const TAG = '[TALENTS_EDIT]';

const FIRST_NAME = 'firstname';
const LAST_NAME = 'lastname';
const PHONES = 'phones';
const EMAILS = 'emails';
const CITY = 'city';
const STATE = 'state';
const COUNTRY = 'country';
const SOCIAL_LINKS = 'socialLinks';
const ATTRIBUTES = 'attributes';
const SKILLS = 'skills';

export const getCompanyNameByEductionItem = (arr: EmplymentHistoryWithId[], id: string): string => {
  if (!id) {
    return '';
  }
  const value = arr.find((values: EmplymentHistoryWithId) => values.id === id);

  return value?.companyName || '';
};
export const getEducationNameByEductionItem = (arr: EducationHistoryWithId[],
  id: string): string => {
  if (!id) {
    return '';
  }
  const value = arr.find((values: EducationHistoryWithId) => values.id === id);

  return value?.institutionName || '';
};

const talentEditPageStyle = {
  box: {
    marginTop: 500,
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    justifyContent: 'center',
  },

};

type TalentsEditProps = {
  data: FormattedTalentResponse & { linkedinUrl?:string },
  isShowUpdateContactInfo?: boolean,
  viewMode: ViewMode,
};

const style = {
  boxRow: {
    sx: {
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  containerMain: {
    sx: {
      pt: 8,
      pb: 8,
      pX: 0,
      mX: 0,
      display: 'flex',
    },
  },
  containerTalentEdit: {
    flex: 1,
  },
  containerTalentEditInner: {
    margin: '0 auto',
    maxWidth: 1100,
  },
  form: {
    width: '100%',
  },
} as const;

const TalentsEdit = ({
  data,
  isShowUpdateContactInfo,
  viewMode,
}: TalentsEditProps):JSX.Element => {
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const { hasPermissions } = useAuth();

  const [valuesField, setValuesFiled] = useState<InitialStateTalentsEditForm>(
    {
      firstname: '',
      lastname: '',
      email: '',
      phone: '',
      city: '',
      state: '',
      country: '',
      emails: [],
      phones: [],
      attributes: [],
      skills: [],
      employmentHistory: [],
      educationHistory: [],
    },
  );
  const [localSocialLinks, setSocialLinks] = useState<SocialLinkItem[]>([]);
  const [formErrors, setFormErrors] = useState<ErrorsValidatationDefauls>({});
  const { city, state, country } = data.location || {};
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [profileImageTempUrl, setProfileImageTempUrl] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string>('');
  const [openCreateNewCompanyModal, setOpenCreateNewCompanyModal] = useState<boolean>(false);
  const [openCreateNewEducationModal, setOpenCreateNewEducationModal] = useState<boolean>(false);
  const [isErrorUploadImage, setIsErrorUploadImage] = useState<
  {error: boolean, errorMessage: string}
  >({
    error: false,
    errorMessage: '',
  });

  useExitPrompt(viewMode === VIEW_MODE.EDITABLE);

  const [openModal, setOpenModal] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data: dataHistory,
    isError: isErrorHistory,
    error: historyError,
    isFetching: isFetchingHistory,
    refetch,
  } = useHistoryLogsTalent(data.talentId || '',
    () => {
      log.debug(TAG, 'loaded talent history data');
    });

  const isHistoryVisible =
    searchParams.get('showHistory') === 'true' &&
    hasPermissions([PERMISSIONS.TALENT_HISTORY_LOG_VIEW]);

  const handleOpenHistoryView = () => {
    setSearchParams({ showHistory: 'true' });
    refetch();
  };
  const handleCloseHistoryView = () => {
    setSearchParams({ showHistory: 'false' });
  };

  useEffect(() => {
    setValuesFiled({
      firstname: data.firstName,
      lastname: data.lastName,
      email: '',
      phone: '',
      city: city || '',
      state: state || '',
      country: country || '',
      emails: data.emails,
      phones: data.phoneNumbers,
      attributes: data.attributes,
      skills: data.technicalSkills,
      employmentHistory: data.employmentHistory,
      educationHistory: data.educationHistory,
    });

    if (data.profileImage) {
      setProfileImageUrl(data.profileImage);
    }

    if (data.profileImageTempUrl) {
      setProfileImageTempUrl(data.profileImageTempUrl);
    }

    if (data.socialLinks) {
      setSocialLinks(data.socialLinks);
    }
  }, [
    data.emails,
    data.firstName,
    data.lastName,
    city,
    country,
    state,
    data.phoneNumbers,
    data.socialLinks,
    data.attributes,
    data.technicalSkills,
    data.employmentHistory,
    data.educationHistory,
    data.profileImage,
    data.profileImageTempUrl,
  ]);

  const handleOpenCreateNewCompanyModal = useCallback((id: string) => {
    setSelectedId(id);
    setOpenCreateNewCompanyModal(true);
  }, []);

  const handleCloseCreateNewCompanyModal = useCallback(() => {
    setOpenCreateNewCompanyModal(false);
  }, []);

  const handleOpenCreateNewEducationModal = useCallback((id: string) => {
    setSelectedId(id);
    setOpenCreateNewEducationModal(true);
  }, []);

  const handleCloseCreateNewEducationModal = useCallback(() => {
    setOpenCreateNewEducationModal(false);
  }, []);

  const getFormattedSocialLink = useCallback((): SocialLinks[] => {
    return localSocialLinks.map((item) => ({
      type: item.socialType,
      url: item.url,
    }));
  }, [localSocialLinks]);

  const updateTalent = usePostTalent(() => navigate(ROUTES.TALENTS));

  const handleFormSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setValuesFiled((values:InitialStateTalentsEditForm) => {
      return {
        ...values,
        [e.target.name]: e.target.value,
      };
    });
  }, []);

  const onLocationChanged = useCallback((location:LocationType) => {
    setValuesFiled((values:InitialStateTalentsEditForm) => {
      return {
        ...values,
        city: location.city,
        state: location.state,
        country: location.country,
      };
    });
  }, []);

  const handleEmailPhoneChanged = useCallback((contact:ContactDetail) => {
    setValuesFiled((prev) => {
      return {
        ...prev,
        emails: contact.emails?.map((item:EmailItem) => ({
          id: item.id,
          value: item.value,
        })) || [],
        phones: contact.phones?.map((item: PhoneItem) => ({
          id: item.id,
          value: item.value,
        })) || [],
      };
    });
  }, []);

  const prepareDateObject = (dateObj: DateObject | null) => {
    // nothing selected
    if (!dateObj) {
      return null;
    }

    // values may have be changed/selected ("touched") but for empty values
    if (!dateObj.month && !dateObj.year) {
      return null;
    }

    return {
      // values are optional, if not selected - don't retrun their values
      month: dateObj.month || undefined,
      year: dateObj.year || undefined,
    };
  };

  const handleSubmitClick = useCallback(() => {
    const errorsObject = validateBasicDetails(valuesField, localSocialLinks);
    setFormErrors(errorsObject);

    if (Object.keys(errorsObject).length === 0) {
      const talentObject: TalentUpdate = createTalentObject({
        talentId: data.talentId,
        latestVersion: data.version,
        firstName: valuesField.firstname,
        lastName: valuesField.lastname,
        phoneNumbers: valuesField.phones?.map((item) => item.value),
        emails: valuesField.emails?.map((item) => item.value),
        city: valuesField.city,
        state: valuesField.state,
        country: valuesField.country,
        socialLinks: getFormattedSocialLink(),
        attributes: valuesField.attributes?.map((item) => ({
          name: item.name,
          description: item.description,
        })) || [],
        technicalSkills: valuesField.skills?.map((item) => ({
          name: item.name,
          level: item.level,
        })) || [],
        // In case we dont have profile image at all we dont need to send profileImageTempUrl and profileImage at all.
        profileImageTempUrl: profileImageTempUrl || undefined,
        profileImage: profileImageUrl || undefined,
        linkedinUrl: data?.linkedinUrl || '',
        employmentHistory: valuesField.employmentHistory?.map((item) => ({
          badges: item.badges,
          companyId: item.companyId,
          companyLogo: item.companyLogo,
          companyName: item.companyName,
          description: item.description || '',
          title: item.title || '',
          startDate: item.startDate,
          endDate: item.endDate,
          keyInsights: item.keyInsights,
        })),
        educationHistory: valuesField.educationHistory?.map((item) => ({
          institutionId: item.institutionId,
          institutionName: item.institutionName,
          institutionLogo: item.institutionLogo,
          title: item.title || '',
          description: item.description || '',
          degree: item.degree,
          fieldOfStudy: item.fieldOfStudy,
          badges: item.badges,
          startDate: prepareDateObject(item.startDate),
          endDate: prepareDateObject(item.endDate),
        })),
      });

      updateTalent.mutate(talentObject);
    } else {
      log.error(TAG, 'form validation falied', errorsObject);
    }
  }, [
    data?.linkedinUrl,
    data.talentId,
    data.version,
    getFormattedSocialLink,
    localSocialLinks,
    profileImageUrl,
    updateTalent,
    valuesField,
    profileImageTempUrl,
  ]);

  const handleAddSocialLink = useCallback(() => {
    setSocialLinks((prevSocialLinks) => [...prevSocialLinks,
      {
        id: uuidv4(),
        socialType: SOCIAL_LINKS_VALUES.OTHER,
        url: '',
      }]);
  }, []);

  const removeSocialLinkHandler = useCallback((itemId: string) => {
    setSocialLinks(localSocialLinks.filter((item) => item.id !== itemId));
  }, [localSocialLinks]);

  const editSocialLinkHandler = useCallback((updatedItem: SocialLinkItem) => {
    const copiedSocialLinks = [...localSocialLinks];
    const updatedSocialLinks = copiedSocialLinks.map((item) => {
      return item.id === updatedItem.id ? updatedItem : item;
    });

    setSocialLinks(updatedSocialLinks);
  }, [localSocialLinks]);

  const editKeyAttributeHandler = useCallback((updatedItem: Attributes) => {
    const copiedAttributesArray = valuesField.attributes ? [...valuesField.attributes] : [];
    const updatedAttributesArray = copiedAttributesArray.map((item) => {
      return item.id === updatedItem.id ? updatedItem : item;
    });

    setValuesFiled((prev) => {
      return {
        ...prev,
        attributes: [...updatedAttributesArray],
      };
    });
  }, [valuesField.attributes]);

  const addKeyAtributeItemHandler = useCallback(() => {
    setValuesFiled((prev:InitialStateTalentsEditForm):InitialStateTalentsEditForm => {
      const attributesCopy = prev.attributes ? [...prev.attributes] : [];
      attributesCopy.push({
        id: uuidv4(),
        name: '',
        description: '',
      });

      return {
        ...prev,
        attributes: attributesCopy,
      };
    });
  }, []);

  const removeKeyAttributeItemHandler = useCallback((itemId: string) => {
    setValuesFiled((prev:InitialStateTalentsEditForm):InitialStateTalentsEditForm => {
      return {
        ...prev,
        attributes: prev.attributes?.filter((item) => item.id !== itemId),
      };
    });
  }, []);

  const editSkillHandler = useCallback((updatedItem: Skills) => {
    const copiedSkillArray = valuesField.skills ? [...valuesField.skills] : [];
    const updatedSkillsArray = copiedSkillArray.map((item) => {
      return item.id === updatedItem.id ? updatedItem : item;
    });

    setValuesFiled((prev:InitialStateTalentsEditForm):InitialStateTalentsEditForm => {
      return {
        ...prev,
        skills: [...updatedSkillsArray],
      };
    });
  }, [valuesField.skills]);

  const addSkillItemHandler = useCallback(() => {
    setValuesFiled((prev:InitialStateTalentsEditForm):InitialStateTalentsEditForm => {
      const skillsCopy = prev.skills ? [...prev.skills] : [];
      skillsCopy.push({
        id: uuidv4(),
        name: '',
        level: 0,
      });

      return {
        ...prev,
        skills: skillsCopy,
      };
    });
  }, []);

  const removeSkillItemHandler = useCallback((itemId: string) => {
    setValuesFiled((prev:InitialStateTalentsEditForm):InitialStateTalentsEditForm => {
      return {
        ...prev,
        skills: prev.skills?.filter((item) => item.id !== itemId),
      };
    });
  }, []);

  const { error } = updateTalent;

  const deleteExperianceChapterHandler = useCallback((id: string) => {
    setValuesFiled((prev) => {
      const filtredArr = prev.employmentHistory.filter((employement) => {
        return employement.id !== id;
      });

      return {
        ...prev,
        employmentHistory: filtredArr,
      };
    });
  }, []);

  const handleDeleteEducationChapter = useCallback((id: string) => {
    setValuesFiled((prev) => {
      const filtredArr = prev.educationHistory.filter((education) => {
        return education.id !== id;
      });

      return {
        ...prev,
        educationHistory: filtredArr,
      };
    });
  }, []);

  const addNewExperienceChapterHandler = useCallback(() => {
    const newChapter:EmplymentHistoryWithId = {
      id: uuidv4(),
      title: '',
      companyId: '',
      companyName: '',
      companyLogo: '',
      description: '',
      badges: [],
      startDate: {
        month: '',
        year: '',
      },
      endDate: {
        month: '',
        year: '',
      },
    };

    setValuesFiled((prev) => {
      return {
        ...prev,
        employmentHistory: [...prev.employmentHistory, newChapter],
      };
    });
  }, []);

  const handleAddNewEducationChapter = useCallback(() => {
    const newChapter:EducationHistoryWithId = {
      id: uuidv4(),
      institutionId: '',
      institutionLogo: '',
      institutionName: '',
      title: '',
      description: '',
      degree: '',
      fieldOfStudy: '',
      badges: [],
      startDate: null,
      endDate: null,
    };

    setValuesFiled((prev) => {
      return {
        ...prev,
        educationHistory: [...prev.educationHistory, newChapter],
      };
    });
  }, []);

  const experienceSelectHandler = useCallback(
    (updatedObject:UpdateValues<string | boolean | null | DateObject>,
      id: string) => {
      setValuesFiled((prev) => {
        return {
          ...prev,
          employmentHistory: prev.employmentHistory.map((item:EmplymentHistoryWithId) => {
            if (item.id !== id) return item;

            return {
              ...item,
              ...updatedObject,
            };
          }),
        };
      });
    }, []);

  const educationSelectHandler = useCallback(
    (updatedObject:UpdateValues<string | boolean | null | DateObject>,
      id: string) => {
      setValuesFiled((prev) => {
        return {
          ...prev,
          educationHistory: prev.educationHistory.map((item:EducationHistoryWithId) => {
            if (item.id !== id) return item;

            return {
              ...item,
              ...updatedObject,
            };
          }),
        };
      });
    }, []);

  if (updateTalent.isLoading) {
    return (
      <Box style={ talentEditPageStyle.box }>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <TalentLeftNavBar
        valuesField={ valuesField }
        isBasicDetailsError={ (
          FIRST_NAME in formErrors ||
          LAST_NAME in formErrors ||
          PHONES in formErrors ||
          EMAILS in formErrors ||
          CITY in formErrors ||
          STATE in formErrors ||
          COUNTRY in formErrors
        ) }
        isSocialLinksError={ SOCIAL_LINKS in formErrors }
        isAtrributesError={ ATTRIBUTES in formErrors }
        isSkillsError={ SKILLS in formErrors }
        socialLinksCounter={ localSocialLinks.length || 0 }
      />
      <Box sx={ style.containerMain.sx }>
        <Box style={ style.containerTalentEdit }>
          <Box style={ style.containerTalentEditInner }>
            <BreadCrumbs
              isNew={ !data?.talentId }
            />
            <TalentEditHeader
              viewMode={ viewMode }
              talentId={ data.talentId }
              onSubmitClick={ handleSubmitClick }
              firstName={ data.firstName }
              lastName={ data.lastName }
              version={ data.version }
              createdBy={ data.createdBy }
              updatedBy={ data.updatedBy }
              updatedAt={ data.updatedAt }
              createdAt={ data.createdAt }
              setProfileImageUrl={ setProfileImageUrl }
              setProfileImageTempUrl={ setProfileImageTempUrl }
              setErrorUploadImage={ setIsErrorUploadImage }
              profileImageUrl={ profileImageUrl }
              profileImageTempUrl={ profileImageTempUrl }
              onViewHistoryClick={ handleOpenHistoryView }
            />

            {updateTalent.isError && (
              <ErrorsStack
                errors={ error?.response?.data?.fieldsErrors || [] }
                errorMessage={ error?.message || '' }
              />
            )}

            {isErrorUploadImage.error && (
              <ErrorsStack
                errors={ [] }
                errorMessage={ isErrorUploadImage.errorMessage }
              />
            )}
            <form
              onSubmit={ handleFormSubmit }
              style={ style.form }
            >
              <TalentBasicDetails
                formErrors={ formErrors }
                onLocationChanged={ onLocationChanged }
                onInputChange={ handleInputChange }
                firstName={ valuesField.firstname }
                lastName={ valuesField.lastname }
                city={ valuesField.city }
                state={ valuesField.state }
                country={ valuesField.country }
                viewMode={ viewMode }
              />

              <TalentContactInfo
                talentId={ data.talentId }
                emails={ valuesField.emails || [] }
                phones={ valuesField.phones || [] }
                handleEmailPhoneChanged={ handleEmailPhoneChanged }
                onUpdateClick={ setOpenModal }
                isOpenModal={ openModal }
                isShowUpdateContactInfo={ !!isShowUpdateContactInfo }
              />
              <TalentSocialLinks
                socialLinks={ localSocialLinks }
                formErrors={ formErrors }
                addSocialLinkHandler={ handleAddSocialLink }
                removeSocialLinkHandler={ removeSocialLinkHandler }
                editSocialLinkHandler={ editSocialLinkHandler }
                viewMode={ viewMode }
              />
              <TalentExperience
                employmentHistory={ valuesField?.employmentHistory || [] }
                onExperienceSelect={ experienceSelectHandler }
                onAddNewExperienceChapter={ addNewExperienceChapterHandler }
                onDeleteExperianceChapter={ deleteExperianceChapterHandler }
                onOpenCreateNewCompanyModal={ handleOpenCreateNewCompanyModal }
                viewMode={ viewMode }
              />
              <TalentEducation
                educationHistory={ valuesField?.educationHistory || [] }
                onDeleteEducationChapter={ handleDeleteEducationChapter }
                onAddNewEducationChapter={ handleAddNewEducationChapter }
                onEducationSelectHandler={ educationSelectHandler }
                onOpenCreateNewEducationModal={ handleOpenCreateNewEducationModal }
                viewMode={ viewMode }
              />
              <TalentKeyAttributes
                attributes={ valuesField.attributes || [] }
                onAddKeyAttributeClick={ addKeyAtributeItemHandler }
                onRemoveKeyAttributeClick={ removeKeyAttributeItemHandler }
                onKeyAttributeChanged={ editKeyAttributeHandler }
                formErrors={ formErrors }
                viewMode={ viewMode }
              />

              <TalentSkills
                skills={ valuesField.skills || [] }
                onAddSkillItem={ addSkillItemHandler }
                onRemoveSkillItem={ removeSkillItemHandler }
                onEditSkill={ editSkillHandler }
                formErrors={ formErrors }
                viewMode={ viewMode }
              />
            </form>
          </Box>
        </Box>
        <HistoryMainPanel
          isError={ isErrorHistory }
          error={ historyError }
          isOpened={ isHistoryVisible }
          onCloseClick={ handleCloseHistoryView }
          data={ dataHistory }
          isFetching={ isFetchingHistory }
          entity={ translate(STRING_KEYS.TALENT) }
        />
        {openCreateNewCompanyModal && (
          <ManualCompanyModal
            isOpen={ openCreateNewCompanyModal }
            onCloseCreateNewCompanyModal={ handleCloseCreateNewCompanyModal }
            selectedId={ selectedId }
            companyFieldName={
              getCompanyNameByEductionItem(valuesField.employmentHistory, selectedId)
            }
            onCompanyCreated={ experienceSelectHandler }
          />
        )}
        {openCreateNewEducationModal && (
          <ManualEducationModal
            isOpen={ openCreateNewEducationModal }
            onCloseCreateNewEducationModal={ handleCloseCreateNewEducationModal }
            selectedId={ selectedId }
            educationFieldName={
              getEducationNameByEductionItem(valuesField.educationHistory, selectedId)
            }
            onEducationCreated={ educationSelectHandler }
          />
        )}
      </Box>
    </>
  );
};

export default TalentsEdit;

TalentsEdit.defaultProps = {
  isShowUpdateContactInfo: false,
};
